<template>
  <div class="cronograma-container">
    <!-- Objeto 1: Filtros e controles -->
    <div class="header-section">
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center mb-4">
              <v-btn icon @click="voltarParaInicio" class="mr-2">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-icon left large color="primary">mdi-calendar-clock</v-icon>
              <span class="text-h5">Gerenciamento de Cronogramas</span>
              <v-spacer></v-spacer>
              <v-btn 
                color="warning" 
                @click="abrirAnaliseAmbiente" 
                class="mr-2 btn-analisar-ambiente"
                style="position: relative;"
              >
                <v-icon left>mdi-alert-circle</v-icon>
                Analisar Ambiente
                <span 
                  v-if="temInconsistencias" 
                  class="notification-badge"
                  title="Inconsistências encontradas!"
                ></span>
              </v-btn>
              <v-btn color="info" @click="abrirCongestionamento" class="mr-2">
                <v-icon left>mdi-chart-line</v-icon>
                Análise de Congestionamento
              </v-btn>
              <v-btn color="primary" @click="carregarCronogramas" :loading="loading">
                <v-icon left>mdi-refresh</v-icon>
                Atualizar
              </v-btn>
            </div>
            
            <div class="filtros-container">
              <v-row>
                <v-col cols="12" sm="6" md="5">
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Pesquisar por host"
                    single-line
                    hide-details
                    clearable
                    outlined
                    dense
                    class="modern-input"
                    placeholder="Digite o nome do host..."
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="5">
                  <v-select
                    v-model="statusFilter"
                    :items="statusFilterOptions"
                    label="Filtrar por status de atualização"
                    outlined
                    dense
                    hide-details
                    @change="applyStatusFilter"
                    class="modern-select"
                    placeholder="Selecione um status..."
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="12" md="2" class="d-flex align-center justify-center">
                  <v-chip 
                    v-if="statusFilter" 
                    color="info" 
                    outlined 
                    small
                    class="status-filter-chip"
                  >
                    <v-icon left small>mdi-filter</v-icon>
                    Filtro ativo
                  </v-chip>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Objeto 2: Tabela de cronogramas -->
    <div class="content-section">
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card class="modern-card" elevation="8">
              <v-card-text class="pa-0">
                <div class="scrollable-table">
                  <v-data-table
                    :headers="headers"
                    :items="filteredCronogramas"
                    :loading="loading"
                    :items-per-page="5"
                    class="modern-table"
                    :class="{ 'table-dark': $vuetify.theme.dark }"
                    :search="search"
                    :custom-filter="customFilter"
                  >

                <template v-slot:item.id="{ item }">
                  <v-chip x-small outlined color="secondary" class="font-weight-medium">
                    {{ item.id }}
                  </v-chip>
                </template>

                <template v-slot:item.hostId="{ item }">
                  <v-chip x-small outlined color="primary" class="font-weight-medium">
                    {{ item.hostId || (item.host ? item.host.hostId : 'N/A') }}
                  </v-chip>
                </template>

                <template v-slot:item.hostName="{ item }">
                  <div class="d-flex align-center">
                    <v-icon left color="primary">mdi-server</v-icon>
                    <span class="font-weight-medium">{{ item.host ? item.host.name : 'N/A' }}</span>
                    <v-btn 
                      icon 
                      small 
                      class="ml-2" 
                      :loading="item.host && item.host.syncing" 
                      @click="syncSingleHost(item.host)"
                      v-if="item.host"
                    >
                      <v-icon>mdi-sync</v-icon>
                    </v-btn>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                          icon 
                          small 
                          class="ml-1" 
                          color="info"
                          @click="openLogsDialog(item.host)"
                          v-bind="attrs"
                          v-on="on"
                          v-if="item.host"
                        >
                          <v-icon>mdi-file-document-multiple</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver logs de cronograma</span>
                    </v-tooltip>
                  </div>
                </template>

                <template v-slot:item.sistemas="{ item }">
                  <div class="d-flex flex-wrap gap-2 pt-2">
                    <!-- VCON -->
                    <div v-if="item.vcon" class="d-flex flex-column align-center mb-2">
                      <v-chip
                        :color="getSystemColor(item, 'VCON')"
                        text-color="white"
                        small
                        class="mb-1"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        VCON
                      </v-chip>
                      <span 
                        v-if="item.horarioVcon" 
                        class="caption text-center"
                      >
                        {{ formatTime(item.horarioVcon) }}
                      </span>
                    </div>

                    <!-- VPRA -->
                    <div v-if="item.vpra" class="d-flex flex-column align-center mb-2">
                      <v-chip
                        :color="getSystemColor(item, 'VPRA')"
                        text-color="white"
                        small
                        class="mb-1"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        VPRA
                      </v-chip>
                      <span 
                        v-if="item.horarioVpra" 
                        class="caption text-center"
                      >
                        {{ formatTime(item.horarioVpra) }}
                      </span>
                    </div>

                    <!-- VSUC -->
                    <div v-if="item.vsuc" class="d-flex flex-column align-center mb-2">
                      <v-chip
                        :color="getSystemColor(item, 'VSUC')"
                        text-color="white"
                        small
                        class="mb-1"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        VSUC
                      </v-chip>
                      <span 
                        v-if="item.horarioVsuc" 
                        class="caption text-center"
                      >
                        {{ formatTime(item.horarioVsuc) }}
                      </span>
                    </div>

                    <!-- VSUP -->
                    <div v-if="item.vsup" class="d-flex flex-column align-center mb-2">
                      <v-chip
                        :color="getSystemColor(item, 'VSUP')"
                        text-color="white"
                        small
                        class="mb-1"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        VSUP
                      </v-chip>
                      <span 
                        v-if="item.horarioVsup" 
                        class="caption text-center"
                      >
                        {{ formatTime(item.horarioVsup) }}
                      </span>
                    </div>

                    <!-- VSYN -->
                    <div v-if="item.vsyn" class="d-flex flex-column align-center mb-2">
                      <v-chip
                        :color="getSystemColor(item, 'VSYN')"
                        text-color="white"
                        small
                        class="mb-1"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        VSYN
                      </v-chip>
                      <span 
                        v-if="item.horarioVsyn" 
                        class="caption text-center"
                      >
                        {{ formatTime(item.horarioVsyn) }}
                      </span>
                    </div>

                    <!-- Mensagem quando nenhum sistema -->
                    <span v-if="!item.vcon && !item.vpra && !item.vsuc && !item.vsup && !item.vsyn" class="text--disabled pt-2">
                      Nenhum sistema selecionado
                    </span>
                  </div>
                </template>


                <template v-slot:item.jobsAgendados="{ item }">
                  <div class="d-flex align-center justify-center">
                    <v-switch
                      :input-value="item.jobsAgendados"
                      @change="toggleCronograma(item, $event)"
                      :loading="item.toggling"
                      :disabled="item.toggling"
                      color="success"
                      dense
                      hide-details
                      class="mt-0 pt-0"
                    >
                      <template v-slot:label>
                        <span class="text-caption" :class="item.jobsAgendados ? 'success--text' : 'error--text'">
                          {{ item.jobsAgendados ? 'ON' : 'OFF' }}
                        </span>
                      </template>
                    </v-switch>
                  </div>
                </template>

                <template v-slot:item.logsVideo="{ item }">
                  <div class="text-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon 
                          :color="item.logsVideo ? 'success' : 'error'" 
                          size="20"
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ item.logsVideo ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                      </template>
                      <span>{{ item.logsVideo ? 'Gravação de vídeo ativada' : 'Gravação de vídeo desativada' }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <template v-slot:item.updatedAt="{ item }">
                  <span class="caption">{{ formatDate(item.updatedAt) }}</span>
                </template>

                <template v-slot:item.statusAtualizacao="{ item }">
                  <div class="d-flex align-center">
                    <v-icon 
                      :color="getStatusAtualizacaoColor(item)" 
                      class="mr-2"
                    >
                      {{ getStatusAtualizacaoIcon(item) }}
                    </v-icon>
                    <span :class="getStatusAtualizacaoColor(item) + '--text'">
                      {{ getStatusAtualizacaoText(item) }}
                    </span>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <div class="d-flex gap-1">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          @click="editarCronograma(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar cronograma</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="confirmarRemocao(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Remover cronograma</span>
                    </v-tooltip>
                  </div>
                </template>
                  </v-data-table>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Dialog para editar cronograma -->
    <CronogramaConfig
      v-model="editDialog"
      :selected-host="cronogramaEditando && cronogramaEditando.host ? cronogramaEditando.host : null"
      :cronograma-data="cronogramaEditando"
      :title="'Editar Cronograma'"
      :loading="salvando"
      @save="salvarCronograma"
      @cancel="editDialog = false"
      ref="cronogramaConfig"
    />


    <!-- Dialog de confirmação de remoção -->
    <v-dialog v-model="confirmDialog" max-width="400px" class="modern-dialog">
      <v-card class="modern-dialog-card">
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirmar Remoção
        </v-card-title>
        
        <v-card-text>
          Tem certeza que deseja remover o cronograma do host 
          <strong>{{ cronogramaParaRemover && cronogramaParaRemover.host ? cronogramaParaRemover.host.name : '' }}</strong>?
          <br><br>
          Esta ação não pode ser desfeita e todos os jobs agendados serão cancelados.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancelar</v-btn>
          <v-btn 
            color="error" 
            @click="removerCronograma"
            :loading="removendo"
          >
            Remover
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificações -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Logs -->
    <log-viewer
      v-model="logsDialog"
      :selected-host="selectedHost"
      @show-snackbar="mostrarSnackbar"
      @redirect-to-login="redirectToLogin"
    />

    <!-- Dialog de congestionamento -->
    <CongestionamentoViewer
      v-model="congestionamentoDialog"
      @show-snackbar="mostrarSnackbar"
    />

    <!-- Dialog de análise de ambiente -->
    <AnalisarAmbiente
      v-model="analiseAmbienteDialog"
      @show-snackbar="mostrarSnackbar"
      @cronogramas-desativados="onCronogramasDesativados"
      @analise-concluida="(dados) => verificarInconsistencias(dados)"
    />
  </div>
</template>

<script>
import hostsService from '@/services/hostsService'
import LogViewer from '@/components/LogViewer.vue'
import CongestionamentoViewer from '@/components/CongestionamentoViewer.vue'
import CronogramaConfig from '@/components/CronogramaConfig.vue'
import AnalisarAmbiente from '@/components/AnalisarAmbiente.vue'
import HorarioValidator from '@/utils/HorarioValidator.js'
import { getSystemMainColor } from '@/utils/systemColors'

export default {
  name: 'CronogramaView',
  components: {
    LogViewer,
    CongestionamentoViewer,
    CronogramaConfig,
    AnalisarAmbiente
  },
  data() {
    return {
      loading: false,
      search: '',
      cronogramas: [],
      tableObserver: null,
      headers: [
        { text: 'ID Host', value: 'hostId', sortable: true, width: '120px' },
        { text: 'Host', value: 'hostName', sortable: true },
        { text: 'Sistemas/Horários', value: 'sistemas', sortable: false },
        { text: 'Jobs Agendados', value: 'jobsAgendados', sortable: true, align: 'center', width: '180px' },
        { text: 'Vídeo', value: 'logsVideo', sortable: true, align: 'center', width: '100px' },
        { text: 'Status Atualização', value: 'statusAtualizacao', sortable: true },
        { text: 'Atualizado em', value: 'updatedAt', sortable: true },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center', width: '180px' }
      ],
      
      // Dialog de edição
      editDialog: false,
      cronogramaEditando: null,
      salvando: false,

      // Dialog de confirmação
      confirmDialog: false,
      cronogramaParaRemover: null,
      removendo: false,

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      // Versões do sistema SCI
      latestVersions: {
        VCON: null,
        VPRA: null,
        VSUC: null,
        VSUP: null,
        VSYN: null
      },
      versionsLoaded: false,

      // Logs - refatorado para usar LogViewer
      logsDialog: false,
      selectedHost: null,

      // Filter
      statusFilter: null,
      statusFilterOptions: [
        { text: 'Todos', value: null },
        { text: 'Atualizado', value: 'Atualizado' },
        { text: 'Desatualizado', value: 'Desatualizado' }
      ],


      // Congestionamento
      congestionamentoDialog: false,

      // Análise de ambiente
      analiseAmbienteDialog: false,
      temInconsistencias: false,
      verificandoInconsistencias: false
    }
  },

  computed: {
    filteredCronogramas() {
      let filtered = this.cronogramas
      
      // Aplicar filtro de status se selecionado
      if (this.statusFilter) {
        filtered = filtered.filter(cronograma => 
          cronograma.statusAtualizacao === this.statusFilter
        )
      }
      
      // Ordenar com desatualizados primeiro
      return filtered.sort((a, b) => {
        const statusOrder = {
          'Desatualizado': 0,
          'Atualizado': 1,
          'Não avaliado': 2
        }
        
        const aOrder = statusOrder[a.statusAtualizacao] || 2
        const bOrder = statusOrder[b.statusAtualizacao] || 2
        
        if (aOrder !== bOrder) {
          return aOrder - bOrder
        }
        
        // Se mesmo status, ordenar por nome do host
        const aHost = a.searchableHostName || ''
        const bHost = b.searchableHostName || ''
        return aHost.localeCompare(bHost)
      })
    },
  },

  watch: {
    filteredCronogramas() {
      this.forceTableHeight()
    },
    cronogramas() {
      this.forceTableHeight()
    }
  },

  mounted() {
    this.loadSystemVersionsFirst()
    this.calculateAvailableHeight()
    window.addEventListener('resize', this.calculateAvailableHeight)
    
    // Verificar inconsistências ao carregar a view
    this.verificarInconsistencias()
    
    // Observer para detectar mudanças na tabela
    this.$nextTick(() => {
      this.setupTableObserver()
    })
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateAvailableHeight)
    
    // Limpar observer da tabela
    if (this.tableObserver) {
      this.tableObserver.disconnect()
    }
  },

  methods: {
    calculateAvailableHeight() {
      this.$nextTick(() => {
        // Altura fixa para exatamente 10 registros
        const rowHeight = 48; // Altura aproximada de cada linha da tabela
        const headerHeight = 48; // Altura do cabeçalho da tabela
        const paginationHeight = 60; // Altura da paginação
        const tablePadding = 8; // Padding interno da tabela reduzido
        
        // Altura total para 10 registros (reduzida para eliminar espaço vazio)
        const fixedTableHeight = headerHeight + (rowHeight * 10) + paginationHeight + tablePadding - 8; // Reduzir 8px para eliminar espaço vazio
        
        const tableElement = document.querySelector('.scrollable-table');
        const tableWrapper = document.querySelector('.scrollable-table .v-data-table__wrapper');
        const tableCard = document.querySelector('.content-section .modern-card');
        
        if (tableElement) {
          tableElement.style.height = `${fixedTableHeight}px`;
          tableElement.style.maxHeight = `${fixedTableHeight}px`;
          tableElement.style.overflow = 'hidden';
        }
        
        if (tableWrapper) {
          const tableContentHeight = fixedTableHeight - paginationHeight;
          tableWrapper.style.height = `${tableContentHeight}px`;
          tableWrapper.style.maxHeight = `${tableContentHeight}px`;
          tableWrapper.style.overflowY = 'auto';
        }
        
        if (tableCard) {
          tableCard.style.height = `${fixedTableHeight}px`;
          tableCard.style.maxHeight = `${fixedTableHeight}px`;
          tableCard.style.overflow = 'hidden';
        }
      });
    },

    setupTableObserver() {
      const tableElement = document.querySelector('.scrollable-table');
      if (tableElement) {
        this.tableObserver = new MutationObserver(() => {
          this.forceTableHeight();
        });
        
        this.tableObserver.observe(tableElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }
    },

    forceTableHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.calculateAvailableHeight();
        }, 100); // Pequeno delay para garantir que o DOM foi atualizado
      });
    },

    async loadSystemVersionsFirst() {
      try {
        // Carregar versões primeiro
        await this.loadSystemVersions()
        // Depois carregar cronogramas
        await this.carregarCronogramas()
      } catch (error) {
        console.error('Erro no carregamento inicial:', error)
        // Se falhar, carregar cronogramas mesmo assim
        this.carregarCronogramas()
      }
    },

    async carregarCronogramas() {
      this.loading = true
      try {
        const response = await hostsService.getCronogramas()
        let cronogramas = response.data || response
        
        // Adicionar campo de busca e status de atualização
        cronogramas = cronogramas.map(cronograma => {
          const processedCronograma = {
            ...cronograma,
            // Converter strings para boolean (alguns valores podem vir como "true"/"false" do banco)
            vcon: cronograma.vcon === true || cronograma.vcon === 'true',
            vpra: cronograma.vpra === true || cronograma.vpra === 'true',
            vsuc: cronograma.vsuc === true || cronograma.vsuc === 'true',
            vsup: cronograma.vsup === true || cronograma.vsup === 'true',
            vsyn: cronograma.vsyn === true || cronograma.vsyn === 'true',
            logsVideo: cronograma.logsVideo === true || cronograma.logsVideo === 'true',
            jobsAgendados: cronograma.jobsAgendados === true || cronograma.jobsAgendados === 'true',
            searchableHostName: cronograma.host ? cronograma.host.name : 'N/A',
            statusAtualizacao: this.calculateStatusAtualizacao(cronograma)
          }
          
          return processedCronograma
        })
        
        this.cronogramas = cronogramas
      } catch (error) {
        console.error('Erro ao carregar cronogramas:', error)
        this.mostrarSnackbar('Erro ao carregar cronogramas', 'error')
      } finally {
        this.loading = false
      }
    },

    editarCronograma(cronograma) {
      // Validar bloqueio de porta antes de abrir o diálogo
      const host = cronograma.host || cronograma
      if (host && host.porta_bloqueio === true) {
        if (host.porta_liberado !== true) {
          this.mostrarSnackbar('Este host possui bloqueio de porta, realize a liberação para poder configurar o cronograma.', 'error')
          return
        }
      }
      
      this.cronogramaEditando = cronograma
      this.editDialog = true
    },

    async salvarCronograma(formData) {
      if (!this.cronogramaEditando) return

      this.salvando = true
      try {
        // Usar o hostId (string do Zabbix) em vez do id interno
        const hostIdToSend = this.cronogramaEditando.host ? 
          this.cronogramaEditando.host.hostId : 
          this.cronogramaEditando.hostId
        
        const dados = {
          hostId: hostIdToSend,
          vcon: formData.vcon,
          vpra: formData.vpra,
          vsuc: formData.vsuc,
          vsup: formData.vsup,
          vsyn: formData.vsyn,
          horarioVcon: formData.vcon ? formData.horarioVcon : null,
          horarioVpra: formData.vpra ? formData.horarioVpra : null,
          horarioVsuc: formData.vsuc ? formData.horarioVsuc : null,
          horarioVsup: formData.vsup ? formData.horarioVsup : null,
          horarioVsyn: formData.vsyn ? formData.horarioVsyn : null,
          logsVideo: formData.logsVideo
        }

        await hostsService.createOrUpdateCronograma(dados)
        this.mostrarSnackbar('Cronograma atualizado com sucesso!', 'success')
        this.editDialog = false
        await this.carregarCronogramas()
      } catch (error) {
        console.error('Erro ao salvar cronograma:', error)
        
        // Verificar se é erro de bloqueio de porta
        if (error.response && error.response.status === 403 && error.response.data.bloqueado) {
          this.mostrarSnackbar(error.response.data.error || 'Este host possui bloqueio de porta, realize a liberação para poder configurar o cronograma.', 'error')
          return
        }
        
        // Verificar se é erro de validação de horários
        if (error.response && error.response.status === 400 && error.response.data.conflitos) {
          this.mostrarSnackbar(`Erro de validação: ${error.response.data.conflitos.join('; ')}`, 'error')
        } else {
          this.mostrarSnackbar('Erro ao salvar cronograma', 'error')
        }
      } finally {
        this.salvando = false
      }
    },

    confirmarRemocao(cronograma) {
      this.cronogramaParaRemover = cronograma
      this.confirmDialog = true
    },

    async removerCronograma() {
      if (!this.cronogramaParaRemover) return

      this.removendo = true
      try {
        await hostsService.deleteCronograma(this.cronogramaParaRemover.id)
        this.mostrarSnackbar('Cronograma removido com sucesso!', 'success')
        this.confirmDialog = false
        await this.carregarCronogramas()
      } catch (error) {
        console.error('Erro ao remover cronograma:', error)
        this.mostrarSnackbar('Erro ao remover cronograma', 'error')
      } finally {
        this.removendo = false
      }
    },

    hostTemSistema(sistema) {
      if (!this.cronogramaEditando || !this.cronogramaEditando.host) {
        return false
      }
      return this.cronogramaEditando.host[`has${sistema}`] || false
    },

    formatTime(timeString) {
      return HorarioValidator.formatTime(timeString);
    },

    extractTime(timeString) {
      return HorarioValidator.extractTime(timeString);
    },

    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleString('pt-BR')
      } catch {
        return dateString
      }
    },

    getSystemColor(item, sistema) {
      if (!item || !item.host) {
        return 'grey'
      }
      
      // NOVO: Se jobsAgendados estiver desativado, retornar cinza (desabilitado)
      const jobsAgendadosAtivo = item.jobsAgendados === true || item.jobsAgendados === 'true'
      if (!jobsAgendadosAtivo) {
        return 'grey'
      }
      
      const host = item.host
      
      // Buscar a versão usando o nome padrão do campo
      const versionFieldName = `${sistema.toLowerCase()}Version`
      const currentVersion = host[versionFieldName]
      
      if (!this.versionsLoaded || !this.latestVersions[sistema]) {
        // Retornar cores padrão se versões não estiverem carregadas
        const defaultColors = {
          'VCON': '#82B982',
          'VPRA': 'info',
          'VSUC': 'success', 
          'VSUP': 'warning',
          'VSYN': '#B2C58A'
        }
        return defaultColors[sistema] || 'grey'
      }
      
      if (!currentVersion) {
        // Retornar cor padrão se não tiver versão
        const defaultColors = {
          'VCON': '#82B982',
          'VPRA': 'info',
          'VSUC': 'success', 
          'VSUP': 'warning',
          'VSYN': '#B2C58A'
        }
        return defaultColors[sistema] || 'grey'
      }
      
      const latestVersion = this.latestVersions[sistema]
      const isUpdated = currentVersion === latestVersion
      
      // Verde = atualizado, Vermelho = desatualizado (apenas se jobsAgendados = true)
      return isUpdated ? 'success' : '#970700'
    },
    getSystemMainColor(sistema) {
      return getSystemMainColor(sistema)
    },

    mostrarSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    },

    async loadSystemVersions() {
      try {
        const response = await hostsService.getSystemVersions()
        
        // Verificar se há erro na resposta
        if (response && response.success === false) {
          throw new Error(response.message || 'Erro ao carregar versões dos sistemas')
        }
        
        if (response.versions) {
          this.latestVersions = response.versions
          this.versionsLoaded = true
        } else if (response && typeof response === 'object') {
          // Talvez as versões estejam diretamente na resposta
          this.latestVersions = response
          this.versionsLoaded = true
        } else {
          throw new Error('Erro ao carregar versões: formato de resposta inválido')
        }
      } catch (error) {
        console.error('Erro ao carregar versões dos sistemas:', error)
        this.mostrarSnackbar('Não foi possível carregar as versões mais recentes dos sistemas', 'warning')
      }
    },

    async syncSingleHost(host) {
      this.$set(host, 'syncing', true)
      try {
        const response = await hostsService.syncSingleHost(host.hostId)
        
        if (response && response.success) {
          // Recarregar cronogramas após sincronização
          await this.carregarCronogramas()
          this.mostrarSnackbar(`Host ${host.name} sincronizado com sucesso!`, 'success')
        } else {
          console.error('[syncSingleHost] Resposta de erro:', response)
          this.mostrarSnackbar((response && response.message) ? response.message : 'Erro ao sincronizar host', 'error')
        }
      } catch (error) {
        console.error('[syncSingleHost] Erro capturado:', error)
        this.mostrarSnackbar((error && error.message) ? error.message : 'Erro ao sincronizar host', 'error')
      } finally {
        this.$set(host, 'syncing', false)
      }
    },

    async openLogsDialog(host) {
      this.selectedHost = host
      this.logsDialog = true
    },

    voltarParaInicio() {
      this.$router.push({ name: 'Home' })
    },

    redirectToLogin() {
      this.$router.push('/login')
    },

    customFilter(value, search, item) {
      if (!search) return true
      
      const searchValue = search.toLowerCase()
      
      // Pesquisar no nome do host
      const hostName = item.searchableHostName || ''
      if (hostName.toLowerCase().includes(searchValue)) {
        return true
      }
      
      return false
    },

    calculateStatusAtualizacao(cronograma) {
      if (!this.versionsLoaded || !cronograma.host) {
        return 'Não avaliado'
      }

      const sistemas = []
      if (cronograma.vcon && cronograma.host.hasVCON) sistemas.push('VCON')
      if (cronograma.vpra && cronograma.host.hasVPRA) sistemas.push('VPRA')
      if (cronograma.vsuc && cronograma.host.hasVSUC) sistemas.push('VSUC')
      if (cronograma.vsup && cronograma.host.hasVSUP) sistemas.push('VSUP')
      if (cronograma.vsyn && cronograma.host.hasVSYN) sistemas.push('VSYN')

      if (sistemas.length === 0) {
        return 'Não avaliado'
      }

      let hasDesatualizado = false
      
      for (const sistema of sistemas) {
        const versionFieldName = `${sistema.toLowerCase()}Version`
        const currentVersion = cronograma.host[versionFieldName]
        const latestVersion = this.latestVersions[sistema]
        
        if (!currentVersion || !latestVersion) {
          continue
        }
        
        if (currentVersion !== latestVersion) {
          hasDesatualizado = true
          break
        }
      }

      return hasDesatualizado ? 'Desatualizado' : 'Atualizado'
    },

    getStatusAtualizacaoColor(item) {
      if (item.statusAtualizacao === 'Atualizado') {
        return 'success'
      } else if (item.statusAtualizacao === 'Desatualizado') {
        return 'error'
      }
      return 'grey'
    },

    getStatusAtualizacaoIcon(item) {
      if (item.statusAtualizacao === 'Atualizado') {
        return 'mdi-check-circle'
      } else if (item.statusAtualizacao === 'Desatualizado') {
        return 'mdi-close-circle'
      }
      return 'mdi-help-circle'
    },

    getStatusAtualizacaoText(item) {
      if (item.statusAtualizacao === 'Atualizado') {
        return 'Atualizado'
      } else if (item.statusAtualizacao === 'Desatualizado') {
        return 'Desatualizado'
      }
      return 'Não avaliado'
    },

    applyStatusFilter() {
      // O computed property filteredCronogramas será reativo automaticamente
      // Não precisamos fazer nada aqui
    },


    abrirCongestionamento() {
      this.congestionamentoDialog = true
    },

    abrirAnaliseAmbiente() {
      this.analiseAmbienteDialog = true
    },

    onCronogramasDesativados() {
      // Recarregar cronogramas quando alguns forem desativados
      this.carregarCronogramas()
      // Verificar inconsistências novamente após desativar
      this.verificarInconsistencias()
    },

    async verificarInconsistencias(dadosAnalise = null) {
      // Se dados da análise já foram fornecidos (via evento), usar diretamente
      if (dadosAnalise) {
        this.temInconsistencias = (dadosAnalise.totalInconsistencias || 0) > 0
        return
      }

      // Evitar múltiplas chamadas simultâneas
      if (this.verificandoInconsistencias) {
        return
      }

      this.verificandoInconsistencias = true
      try {
        const response = await hostsService.analisarAmbiente()
        
        // O interceptor do axios já retorna response.data, então response já é o objeto de dados
        if (response && response.success !== undefined) {
          this.temInconsistencias = (response.totalInconsistencias || 0) > 0
        } else {
          // Se a resposta não tiver o formato esperado, verificar diretamente
          this.temInconsistencias = (response?.totalInconsistencias || 0) > 0
        }
      } catch (error) {
        console.error('[CronogramaView] Erro ao verificar inconsistências:', error)
        // Em caso de erro, não mostrar notificação (evitar falsos positivos)
        this.temInconsistencias = false
      } finally {
        this.verificandoInconsistencias = false
      }
    },

    async toggleCronograma(cronograma, novoValor) {
      // Adicionar propriedade reativa para controlar loading
      this.$set(cronograma, 'toggling', true)
      
      try {
        const response = await hostsService.toggleCronograma(cronograma.id, novoValor)
        
        if (response && response.success) {
          // Atualizar o valor local
          cronograma.jobsAgendados = novoValor
          
          this.mostrarSnackbar(
            `Cronograma ${novoValor ? 'ativado' : 'desativado'} com sucesso!`,
            'success'
          )
          
          // Recarregar cronogramas para garantir sincronização
          await this.carregarCronogramas()
        } else {
          this.mostrarSnackbar(
            response.message || 'Erro ao alternar status do cronograma',
            'error'
          )
          // Reverter valor em caso de erro
          cronograma.jobsAgendados = !novoValor
        }
      } catch (error) {
        console.error('Erro ao alternar status do cronograma:', error)
        this.mostrarSnackbar(
          'Erro ao alternar status do cronograma',
          'error'
        )
        // Reverter valor em caso de erro
        cronograma.jobsAgendados = !novoValor
      } finally {
        this.$set(cronograma, 'toggling', false)
      }
    }
  }
}
</script>

<style scoped>
/* Container principal que ocupa toda a altura disponível */
.cronograma-container {
  height: calc(100vh - 64px - 16px - 36px -10px); /* 64px para v-app-bar + 16px para padding do v-main + 36px para footer + 10px para padding do v-container */
  max-height: calc(100vh - 64px - 16px - 36px -10px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Seção do cabeçalho - fixa no topo */
.header-section {
  flex-shrink: 0;
  background: transparent;
  z-index: 10;
}

.header-section .v-container {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

/* Seção de conteúdo - altura automática baseada no conteúdo */
.content-section {
  flex: 0 0 auto; /* Não expandir, usar altura do conteúdo */
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.content-section .v-container {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
  flex: 0 0 auto; /* Não expandir, usar altura do conteúdo */
  display: flex;
  flex-direction: column;
  overflow: visible;
}

/* Card da tabela */
.content-section .modern-card {
  flex: 0 0 auto; /* Não expandir, usar altura do conteúdo */
  display: flex;
  flex-direction: column;
  overflow: visible;
  min-height: 0;
}

/* Tabela com altura fixa para 10 registros */
.scrollable-table {
  height: auto !important;
  max-height: none !important;
  overflow: visible;
}

.scrollable-table .v-data-table__wrapper {
  height: auto !important;
  max-height: none !important;
  overflow-y: auto !important;
}

.scrollable-table .v-data-table__wrapper table {
  height: auto !important;
}

/* Garantir que o cabeçalho da tabela fique fixo */
.scrollable-table .v-data-table__wrapper table thead {
  position: sticky;
  top: 0;
  z-index: 5;
  background: inherit;
}

/* Garantir que o cabeçalho da tabela tenha fundo sólido */
.scrollable-table .v-data-table__wrapper table thead tr {
  background: inherit !important;
}

.scrollable-table .v-data-table__wrapper table thead tr th {
  background: inherit !important;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1) !important;
}

/* Melhorar a aparência do scroll */
.scrollable-table .v-data-table__wrapper::-webkit-scrollbar {
  width: 8px;
}

.scrollable-table .v-data-table__wrapper::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.scrollable-table .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.scrollable-table .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

/* Tema escuro para scroll */
.theme-dark .scrollable-table .v-data-table__wrapper::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.theme-dark .scrollable-table .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
}

.theme-dark .scrollable-table .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Tema escuro para cabeçalho da tabela */
.theme-dark .scrollable-table .v-data-table__wrapper table thead tr th {
  background: inherit !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1) !important;
}

.gap-1 {
  gap: 4px;
}

.cursor-pointer {
  cursor: pointer;
}

/* Estilos modernos para cards */
.modern-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 24px !important;
}

/* Tema escuro para cards */
.theme-dark .modern-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.9) 0%, rgba(45, 45, 45, 0.8) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .modern-card-title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Tabela moderna */
.modern-table {
  border-radius: 12px !important;
  overflow: hidden;
  background: transparent !important;
}

.modern-table .v-data-table__wrapper {
  border-radius: 12px;
}

.modern-table .v-data-table__wrapper table {
  border-radius: 12px;
}

.modern-table .v-data-table__wrapper table thead tr {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-table .v-data-table__wrapper table thead tr th {
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  font-weight: 600;
  color: #495057;
}

.modern-table .v-data-table__wrapper table tbody tr {
  transition: all 0.2s ease;
}

.modern-table .v-data-table__wrapper table tbody tr:hover {
  background: rgba(0, 123, 255, 0.05) !important;
  transform: scale(1.01);
}

/* Tema escuro para tabela */
.table-dark .v-data-table__wrapper table thead tr {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
}

.table-dark .v-data-table__wrapper table thead tr th {
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.table-dark .v-data-table__wrapper table tbody tr:hover {
  background: rgba(33, 150, 243, 0.1) !important;
}

/* Diálogos modernos */
.modern-dialog .v-dialog__content {
  backdrop-filter: blur(5px);
}

.modern-dialog-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
}

.modern-dialog-card .v-card__title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 24px !important;
}

/* Tema escuro para diálogos */
.theme-dark .modern-dialog-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .modern-dialog-card .v-card__title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Botões modernos */
.v-btn {
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.v-btn.v-btn--contained {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1) 100%);
  box-shadow: 0 4px 15px rgba(25, 118, 210, 0.3);
}

.v-btn.v-btn--contained:hover {
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.4);
}

/* Campos de entrada modernos */
.v-text-field .v-input__control .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-text-field .v-input__control .v-input__slot:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.v-select .v-input__control .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-select .v-input__control .v-input__slot:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Chips modernos */
.v-chip {
  border-radius: 20px !important;
  transition: all 0.3s ease;
  font-weight: 500;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Animações de entrada */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modern-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Responsividade melhorada */
@media (max-width: 768px) {
  .modern-card-title {
    padding: 16px 20px !important;
  }
  
  .modern-dialog-card .v-card__title {
    padding: 16px 20px !important;
  }
  
  .v-btn {
    font-size: 0.875rem !important;
  }
}

/* Estilos para os campos de filtro */
.filtros-container {
  background: linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(233, 236, 239, 0.8) 100%);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
}

.theme-dark .filtros-container {
  background: linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(61, 61, 61, 0.8) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modern-input .v-input__control .v-input__slot {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.modern-input .v-input__control .v-input__slot:hover {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.modern-input .v-input__control .v-input__slot:focus-within {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.3);
}

.modern-select .v-input__control .v-input__slot {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.modern-select .v-input__control .v-input__slot:hover {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.modern-select .v-input__control .v-input__slot:focus-within {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.3);
}

/* Tema escuro para campos de filtro */
.theme-dark .modern-input .v-input__control .v-input__slot {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.theme-dark .modern-input .v-input__control .v-input__slot:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(100, 181, 246, 0.2) !important;
}

.theme-dark .modern-input .v-input__control .v-input__slot:focus-within {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: #64b5f6 !important;
  box-shadow: 0 0 0 2px rgba(100, 181, 246, 0.3) !important;
}

.theme-dark .modern-select .v-input__control .v-input__slot {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.theme-dark .modern-select .v-input__control .v-input__slot:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(100, 181, 246, 0.2) !important;
}

.theme-dark .modern-select .v-input__control .v-input__slot:focus-within {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: #64b5f6 !important;
  box-shadow: 0 0 0 2px rgba(100, 181, 246, 0.3) !important;
}

/* Labels dos campos no tema escuro */
.theme-dark .modern-input .v-label,
.theme-dark .modern-select .v-label {
  color: rgba(255, 255, 255, 0.87) !important;
}

.theme-dark .modern-input .v-label--active,
.theme-dark .modern-select .v-label--active {
  color: #64b5f6 !important;
}

/* Chip de filtro ativo */
.status-filter-chip {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.2) 100%) !important;
  border: 1px solid rgba(33, 150, 243, 0.3) !important;
  color: #1976d2 !important;
  font-weight: 500;
  animation: pulse 2s infinite;
}

.theme-dark .status-filter-chip {
  background: linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(100, 181, 246, 0.2) 100%) !important;
  border: 1px solid rgba(100, 181, 246, 0.3) !important;
  color: #64b5f6 !important;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsividade dos filtros */
@media (max-width: 960px) {
  .filtros-container {
    padding: 16px;
  }
  
  .filtros-container .v-col {
    margin-bottom: 12px;
  }
}

@media (max-width: 600px) {
  .filtros-container {
    padding: 12px;
    margin-bottom: 16px;
  }
  
  .filtros-container .v-col {
    margin-bottom: 8px;
  }
  
  .status-filter-chip {
    font-size: 0.75rem;
  }
}

/* Badge de inconsistência no botão Analisar Ambiente */
.btn-analisar-ambiente {
  position: relative;
}

/* A bolinha de notificação (red dot) */
.notification-badge {
  position: absolute;
  top: -13px;
  right: -20px;
  width: 13px;
  height: 13px;
  background-color: #ff3b30;
  border: 1px solid #1a1a1a;
  border-radius: 50%;
  z-index: 10;
}

.theme--dark .notification-badge {
  border-color: #1a1a1a;
}

.theme--light .notification-badge {
  border-color: #ffffff;
}

/* O efeito de onda (ripple) */
.notification-badge::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background-color: #ff3b30;
  border-radius: 50%;
  opacity: 0.6;
  z-index: -1;
  /* Animação da onda expandindo */
  animation: ripple 2s infinite cubic-bezier(0, 0, 0.2, 1);
}

/* Faz a bolinha crescer e diminuir levemente */
@keyframes heartbeat {
  0% { 
    transform: scale(1); 
  }
  50% { 
    transform: scale(1.1); 
  }
  100% { 
    transform: scale(1); 
  }
}

/* Faz o anel sair da bolinha e desaparecer */
@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
  }
}
</style>